/**
 * Shared product slider component used by AutoRelated, RelatedProducts, UpsellProducts, CrossSellProducts
 * Eliminates code duplication across 4 nearly identical components
 */
import { ProductCarousel } from './ProductCarousel'
import { Skeleton, useWindowSize, useProductViewSkeletonVariants } from '../../../../Theme'
import { filterInStockProducts, sliceProductsByDevice } from '../../../lib'

import type { RelatedProductItem, AutoRelatedProduct } from '../../../types'

function ProductSliderSkeleton({ columns }: { columns: number }) {
  const {
    skeletonSectionVariants,
    skeletonSliderGridVariants,
    skeletonSliderItemVariants,
  } = useProductViewSkeletonVariants()

  return (
    <div className={skeletonSectionVariants({ section: 'slider' })}>
      <Skeleton size="tab" width="full" />
      <div className={skeletonSliderGridVariants()}>
        {Array.from({ length: columns }).map((_, i) => (
          <div key={i} className={skeletonSliderItemVariants()}>
            <Skeleton size="fill" width="full" />
          </div>
        ))}
      </div>
    </div>
  )
}

interface ProductSliderProps {
  products: RelatedProductItem[] | AutoRelatedProduct[] | undefined | null
  loading: boolean
  title: string
}

export function ProductSlider({ products, loading, title }: ProductSliderProps) {
  const { isMobile, isTablet } = useWindowSize()
  const columns = isMobile ? 2 : isTablet ? 3 : 5

  if (loading) return <ProductSliderSkeleton columns={columns} />

  const availableProducts = filterInStockProducts(products)

  if (availableProducts.length === 0) return null

  return (
    <ProductCarousel
      title={title}
      columns={columns}
      products={sliceProductsByDevice(availableProducts, isMobile, isTablet)}
    />
  )
}
